import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { backofficeApi, formatDateTime } from '@kardume/services';
import type { AuditLogDto, ComboItem } from '@kardume/types';
import {
  PageHeader,
  SearchInput,
  SearchableComboBox,
  Table,
  TableContainer,
  Badge,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import {
  formatAuditAction,
  moduleLabel,
  actionsForModule,
  AUDIT_MODULE_OPTIONS,
} from '../utils/auditActions';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  /* Iguala a altura dos controles. O SearchBox (combobox) põe a borda no wrapper
     em volta do input, então seu input interno fica em 40px (+2px da borda = 42px);
     o input da busca livre tem a borda nele mesmo, então vai a 42px (border-box).
     Resultado: todos os campos com 42px de altura. */
  input {
    box-sizing: border-box;
    height: 40px;
  }
  & > div:first-child input {
    height: 42px;
  }
`;

const FilterField = styled.div`
  flex: 1 1 220px;
  min-width: 200px;
`;

/**
 * Espaçador invisível com a mesma métrica do label dos comboboxes
 * (SearchableComboBox: label 0.75rem/600 + gap 0.25rem), para alinhar o campo de
 * busca livre - que não tem label - com os demais filtros.
 */
const LabelSpacer = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  visibility: hidden;
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const ResourceId = styled.span`
  display: block;
  font-size: 0.75rem;
  color: ${(p) => p.theme.textMuted};
`;

const MODULE_ITEMS: ComboItem[] = AUDIT_MODULE_OPTIONS.map((o) => ({ id: o.value, label: o.label }));

/**
 * Backoffice audit trail. Paginado no servidor (GET /v1/backoffice/audit-logs
 * aceita page/size/module/actor/resourceId/action/search). O filtro de Módulo é
 * obrigatório e define de onde vêm as opções de Responsável (funcionários do
 * backoffice, clientes ou lojas).
 */
const AuditLogsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [module, setModule] = useState<string>('BACKOFFICE');
  const [responsibleId, setResponsibleId] = useState<string>('');
  const [actionValue, setActionValue] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  // Opções do combobox de Responsável, conforme o módulo selecionado.
  const responsibleState = useAsync<ComboItem[]>(async () => {
    if (module === 'CUSTOMER') {
      const pageData = await backofficeApi.listCustomers({ size: 200 });
      return pageData.content.map((c) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`.trim() }));
    }
    if (module === 'MERCHANT') {
      const pageData = await backofficeApi.listMerchants({ size: 200 });
      return pageData.content.map((m) => ({ id: m.id, label: m.tradeName || m.name }));
    }
    const pageData = await backofficeApi.listEmployees({ size: 200 });
    return pageData.content.map((e) => ({ id: e.id, label: `${e.firstName} ${e.lastName}`.trim() }));
  }, [module]);

  const responsibleItems = responsibleState.data ?? [];
  const actionItems = useMemo<ComboItem[]>(() => actionsForModule(module), [module]);
  const isMerchant = module === 'MERCHANT';

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listAuditLogs({
        page,
        size: pageSize,
        module,
        actor: !isMerchant && responsibleId ? responsibleId : undefined,
        resourceId: isMerchant && responsibleId ? responsibleId : undefined,
        action: actionValue || undefined,
        search: debouncedSearch || undefined,
      }),
    [page, pageSize, module, responsibleId, actionValue, debouncedSearch]
  );

  const logs = useMemo<AuditLogDto[]>(() => data?.content ?? [], [data]);
  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalElements ?? 0;

  const handleModuleChange = (value: string) => {
    // Obrigatório: se limparem, volta ao padrão.
    setModule(value || 'BACKOFFICE');
    setResponsibleId('');
    setActionValue('');
    setPage(0);
  };

  const handleResponsibleChange = (id: string) => {
    setResponsibleId(id);
    setPage(0);
  };

  const handleActionChange = (id: string) => {
    setActionValue(id);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  return (
    <>
      <PageHeader
        title="Logs"
        subtitle="Registro de auditoria das ações realizadas no Kardume Urbano."
      />

      <Toolbar>
        <FilterField>
          <LabelSpacer aria-hidden>&nbsp;</LabelSpacer>
          <SearchInput
            placeholder="Busca livre..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Módulo"
            required
            items={MODULE_ITEMS}
            valueId={module}
            onChange={handleModuleChange}
            placeholder="Selecionar módulo..."
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label={isMerchant ? 'Loja' : 'Responsável'}
            items={responsibleItems}
            valueId={responsibleId}
            onChange={handleResponsibleChange}
            placeholder={isMerchant ? 'Selecionar loja...' : 'Selecionar responsável...'}
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Ação"
            items={actionItems}
            valueId={actionValue}
            onChange={handleActionChange}
            placeholder="Selecionar ação..."
          />
        </FilterField>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={logs.length === 0}
        emptyTitle="Nenhum log encontrado"
        emptyDescription="As ações auditáveis realizadas no painel aparecerão aqui."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Módulo</th>
                <th>Responsável</th>
                <th>Ação</th>
                <th>Recurso</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDateTime(log.createdAt)}</td>
                  <td>
                    <Badge tone="secondary">{moduleLabel(log.module)}</Badge>
                  </td>
                  <td>
                    <strong>{log.actorName}</strong>
                  </td>
                  <td>
                    <Badge tone="info">{formatAuditAction(log.action)}</Badge>
                  </td>
                  <td>
                    {log.resourceType}
                    {log.resourceId && <ResourceId>{log.resourceId}</ResourceId>}
                  </td>
                  <td>{log.ip || <Muted>-</Muted>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {!error && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </>
  );
};

export default AuditLogsPage;
